import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  OutlinedInput,
  FormHelperText,
  Divider,
  Alert,
  CircularProgress,
  ListSubheader,
  Skeleton,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { useRoles, useUserRoles, groupRolesByCategory, CATEGORY_LABELS } from '../../hooks/useRoles';
import { useLocations, useUserLocations } from '../../hooks/useUserLocations';
import type { RoleCategory, StaffSalaryConfig } from '../../types';
import { RecurringFrequencyLabels } from '../../types';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
  }).format(amount);
};

interface StaffFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roleIds: string[];
  locationIds: string[];
}

export const StaffEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const { data: roles, isLoading: isLoadingRoles } = useRoles();
  const { data: locations, isLoading: isLoadingLocations } = useLocations();
  const { data: userLocations, isLoading: isLoadingUserLocations } = useUserLocations(id || '');
  const { data: userRoles, isLoading: isLoadingUserRoles } = useUserRoles(id || '');

  // Fetch salary configuration for this staff member
  const { data: salaryConfig, isLoading: isLoadingSalary } = useQuery<StaffSalaryConfig | null>({
    queryKey: ['salary-config', id],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get('/expenses/salary-configs', {
          params: { userId: id, activeOnly: true },
        });
        const configs = Array.isArray(response.data) ? response.data : response.data.items || [];
        // Return the active config for this user (most recent without endDate)
        return configs.find((c: StaffSalaryConfig) => c.userId === id && !c.endDate) || null;
      } catch {
        return null;
      }
    },
    enabled: !!id,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<StaffFormData>({
    defaultValues: {
      roleIds: [],
      locationIds: [],
    },
  });

  const selectedRoleIds = watch('roleIds');

  // Group roles by category for display
  const rolesByCategory = roles ? groupRolesByCategory(roles) : null;

  // Fetch user data
  const { data, isLoading, error } = useQuery({
    queryKey: ['staff', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/users/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  // Populate form when data loads
  useEffect(() => {
    if (data) {
      reset({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        roleIds: [],
        locationIds: [],
      });
    }
  }, [data, reset]);

  // Update roleIds when userRoles loads
  useEffect(() => {
    if (userRoles) {
      const roleIds = userRoles.map((role) => role.id);
      setValue('roleIds', roleIds);
    }
  }, [userRoles, setValue]);

  // Update locationIds when userLocations loads
  useEffect(() => {
    if (userLocations) {
      const locationIds = userLocations.map((ul) => ul.locationId);
      setValue('locationIds', locationIds);
    }
  }, [userLocations, setValue]);

  const updateMutation = useMutation({
    mutationFn: async (formData: StaffFormData) => {
      // Update user basic data (no roleId - roles assigned separately)
      const userData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
      };

      await axiosInstance.patch(`/users/${id}`, userData);

      // Update multi-role assignments
      await axiosInstance.post(`/permissions/users/${id}/roles`, {
        roleIds: formData.roleIds,
      });

      // Update location assignments
      await axiosInstance.post(`/permissions/users/${id}/locations`, {
        locationIds: formData.locationIds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', id] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      queryClient.invalidateQueries({ queryKey: ['userRoles', id] });
      queryClient.invalidateQueries({ queryKey: ['userLocations', id] });
      showMessage('Staff member updated successfully', 'success');
      navigate('/app/staff');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update staff member', 'error');
    },
  });

  const onSubmit = (formData: StaffFormData) => {
    updateMutation.mutate(formData);
  };

  // Get selected roles for display
  const selectedRoles = roles?.filter((r) => selectedRoleIds.includes(r.id)) || [];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load staff member'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Edit Staff Member
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Basic Information */}
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Basic Information" />
          <CardContent>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <TextField
                {...register('firstName', { required: 'First name is required' })}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                fullWidth
                label="First Name"
              />

              <TextField
                {...register('lastName', { required: 'Last name is required' })}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                fullWidth
                label="Last Name"
              />

              <TextField
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
                label="Email"
                type="email"
              />

              <TextField
                {...register('phone', { required: 'Phone is required' })}
                error={!!errors.phone}
                helperText={errors.phone?.message}
                fullWidth
                label="Phone"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Role & Permissions */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Roles & Permissions"
            subheader="Assign one or more roles. Permissions are combined (UNION) from all assigned roles."
          />
          <Divider />
          <CardContent>
            {/* Multi-Role Selection */}
            <Controller
              name="roleIds"
              control={control}
              rules={{ required: 'At least one role is required' }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                  <InputLabel>Roles</InputLabel>
                  <Select
                    {...field}
                    multiple
                    label="Roles"
                    input={<OutlinedInput label="Roles" />}
                    disabled={isLoadingRoles || isLoadingUserRoles}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((roleId) => {
                          const role = roles?.find((r) => r.id === roleId);
                          return (
                            <Chip
                              key={roleId}
                              label={role?.name || roleId}
                              size="small"
                              color={
                                role?.category === 'administrative'
                                  ? 'primary'
                                  : role?.category === 'operational'
                                  ? 'secondary'
                                  : 'default'
                              }
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {/* Group roles by category */}
                    {rolesByCategory &&
                      (['administrative', 'operational', 'combined'] as RoleCategory[]).flatMap((category) => {
                        const categoryRoles = rolesByCategory[category];
                        if (!categoryRoles?.length) return [];
                        return [
                          <ListSubheader key={`header-${category}`} sx={{ bgcolor: 'background.paper' }}>
                            {CATEGORY_LABELS[category]}
                          </ListSubheader>,
                          ...categoryRoles.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                {role.name}
                                {role.isSystemDefault && (
                                  <Chip label="Default" size="small" variant="outlined" />
                                )}
                                {role.isAdmin && (
                                  <Chip label="Admin" size="small" color="error" />
                                )}
                              </Box>
                            </MenuItem>
                          )),
                        ];
                      })}
                  </Select>
                  {fieldState.error ? (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  ) : (
                    <FormHelperText>
                      Select multiple roles to combine their permissions. Use Ctrl/Cmd+Click to select multiple.
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {/* Display selected roles info */}
            {selectedRoles.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {selectedRoles.length} role{selectedRoles.length > 1 ? 's' : ''} selected:
                </Typography>
                {selectedRoles.map((role) => (
                  <Box key={role.id} sx={{ mb: 1 }}>
                    <strong>{role.name}</strong>
                    {role.description && `: ${role.description}`}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Category: {role.category || 'combined'} | {role.rolePermissions?.length || 0} permissions
                    </Typography>
                  </Box>
                ))}
              </Alert>
            )}

            {(isLoadingRoles || isLoadingUserRoles) && (
              <Box display="flex" alignItems="center" gap={1} mt={2}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading roles...</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Location Access */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Location Access"
            subheader="Select which locations this staff member can access"
          />
          <Divider />
          <CardContent>
            <Controller
              name="locationIds"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Assigned Locations</InputLabel>
                  <Select
                    {...field}
                    multiple
                    input={<OutlinedInput label="Assigned Locations" />}
                    disabled={isLoadingLocations || isLoadingUserLocations}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((locationId) => {
                          const location = locations?.find((l) => l.id === locationId);
                          return (
                            <Chip
                              key={locationId}
                              label={location?.name || locationId}
                              size="small"
                              color={location?.type === 'store' ? 'primary' : 'secondary'}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {locations?.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                        <Chip
                          label={location.type}
                          size="small"
                          sx={{ ml: 1 }}
                          variant="outlined"
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Staff will only see data from their assigned locations. Leave empty for access based on their default location.
                  </FormHelperText>
                </FormControl>
              )}
            />

            {(isLoadingLocations || isLoadingUserLocations) && (
              <Box display="flex" alignItems="center" gap={1} mt={2}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading locations...</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Salary Configuration */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Salary Configuration"
            subheader="View and manage salary settings for this staff member"
            avatar={<AccountBalanceWalletIcon color="primary" />}
          />
          <Divider />
          <CardContent>
            {isLoadingSalary ? (
              <Box>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
              </Box>
            ) : salaryConfig ? (
              <Box>
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr 1fr' }} gap={2} mb={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Base Salary
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(salaryConfig.baseSalary)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payment Frequency
                    </Typography>
                    <Typography variant="body1">
                      {RecurringFrequencyLabels[salaryConfig.paymentFrequency]}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Effective Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(salaryConfig.effectiveDate).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AccountBalanceWalletIcon />}
                  onClick={() => navigate('/app/expenses')}
                >
                  Manage Salary in Expenses
                </Button>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  No salary configuration found for this staff member.
                </Alert>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AccountBalanceWalletIcon />}
                  onClick={() => navigate('/app/expenses')}
                >
                  Configure Salary in Expenses
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Box display="flex" gap={2}>
          <Button
            type="submit"
            variant="contained"
            startIcon={updateMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate('/app/staff')}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
